import { Link } from '@inertiajs/react';
import clsx from 'clsx';
import {
    AlertTriangle,
    ArrowLeft,
    CheckCircle,
    Clock,
    Lock,
    Trophy,
} from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import Breadcrumbs from '@/components/Breadcrumbs';
import { useThemeColors } from '@/hooks/useThemeColors';
import { SectionData, UnlockCondition } from '@/types';

interface LockedSectionPageProps {
    section: SectionData;
    courseSlug: string;
    courseTitle: string;
}

const LockedSectionPage: React.FC<LockedSectionPageProps> = ({
    section,
    courseSlug,
    courseTitle,
}) => {
    const { t } = useTranslation();
    const colors = useThemeColors();

    const breadcrumbItems = [
        {
            label: t('course.sectionDetail.courses'),
            href: '/my-account?tab=courses',
        },
        { label: courseTitle, href: `/courses/${courseSlug}` },
        { label: section.title },
    ];

    const getConditionIcon = (condition: UnlockCondition) => {
        if (condition.is_met) {
            return <CheckCircle className="h-6 w-6 text-green-500" />;
        }

        switch (condition.type) {
            case 'evaluations':
                return <AlertTriangle className="h-6 w-6 text-orange-500" />;
            case 'sections':
                return <Lock className={`h-6 w-6 ${colors.iconColor}`} />;
            case 'days_after_enrollment':
                return <Clock className="h-6 w-6 text-purple-500" />;
            case 'rank':
                return <Trophy className="h-6 w-6 text-yellow-500" />;
            case 'manual':
                return <Lock className="h-6 w-6 text-gray-500" />;
            default:
                return <Lock className="h-6 w-6 text-gray-500" />;
        }
    };

    const getConditionDescription = (condition: UnlockCondition) => {
        return condition.description;
    };

    const getConditionHelp = (condition: UnlockCondition) => {
        switch (condition.type) {
            case 'evaluations':
                return t('section.unlock.help.evaluations');
            case 'sections':
                return t('section.unlock.help.sections');
            case 'days_after_enrollment':
                return t('section.unlock.help.timeWait');
            case 'rank':
                return t('section.unlock.help.rank');
            case 'manual':
                return t('section.unlock.help.manual');
            default:
                return '';
        }
    };

    const allConditionsMet = section.unlock_conditions.every(
        (condition) => condition.is_met
    );
    const hasConditions = section.unlock_conditions.length > 0;

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6 lg:px-8">
                {/* Breadcrumbs */}
                <div className="mb-6">
                    <Breadcrumbs items={breadcrumbItems} />
                </div>

                {/* Back to Course Link */}
                <div className="mb-6">
                    <Link
                        href={`/courses/${courseSlug}`}
                        className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors duration-200 ${colors.border} ${colors.buttonSecondary}`}
                    >
                        <ArrowLeft size={16} />
                        <span className="font-medium">
                            {t('course.backToCourse')}
                        </span>
                    </Link>
                </div>

                {/* Main Content */}
                <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                    {/* Header */}
                    <div className="border-b border-red-200 bg-red-50 p-6">
                        <div className="flex items-center space-x-4">
                            <div className="flex-shrink-0">
                                <Lock className="h-8 w-8 text-red-500" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">
                                    {section.title}
                                </h1>
                                <p className="font-medium text-red-700">
                                    {t('section.locked.message')}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="p-6">
                        {!hasConditions ? (
                            /* Manual unlock required */
                            <div className="py-8 text-center">
                                <div className="mx-auto max-w-md">
                                    <AlertTriangle className="mx-auto mb-4 h-12 w-12 text-orange-500" />
                                    <h3 className="mb-2 text-lg font-semibold text-gray-900">
                                        {t('section.unlock.manual.title')}
                                    </h3>
                                    <p className="mb-6 text-gray-600">
                                        {t('section.unlock.manual.description')}
                                    </p>
                                    <div className={`rounded-lg border p-4 ${colors.border} ${colors.accentBg}`}>
                                        <p className={`text-sm ${colors.accentText}`}>
                                            {t(
                                                'section.unlock.manual.contactInstructor'
                                            )}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            /* Requirements list */
                            <div>
                                <div className="mb-6">
                                    <h3 className="mb-2 text-lg font-semibold text-gray-900">
                                        {t('section.unlock.requirements')}
                                    </h3>
                                    <p className="text-gray-600">
                                        {allConditionsMet
                                            ? t('section.unlock.allMet')
                                            : t('section.unlock.description')}
                                    </p>
                                </div>

                                <div className="space-y-4">
                                    {section.unlock_conditions.map(
                                        (condition, index) => (
                                            <div
                                                key={index}
                                                className={clsx(
                                                    'rounded-lg border p-4',
                                                    condition.is_met
                                                        ? 'border-green-200 bg-green-50'
                                                        : 'border-orange-200 bg-orange-50'
                                                )}
                                            >
                                                <div className="flex items-start space-x-3">
                                                    <div className="mt-0.5 flex-shrink-0">
                                                        {getConditionIcon(
                                                            condition
                                                        )}
                                                    </div>
                                                    <div className="flex-1">
                                                        <div className="mb-1 flex items-center space-x-2">
                                                            <h4
                                                                className={clsx(
                                                                    'font-medium',
                                                                    condition.is_met
                                                                        ? 'text-green-900'
                                                                        : 'text-orange-900'
                                                                )}
                                                            >
                                                                {getConditionDescription(
                                                                    condition
                                                                )}
                                                            </h4>
                                                            <span
                                                                className={clsx(
                                                                    'rounded-full px-2 py-1 text-xs font-medium',
                                                                    condition.is_met
                                                                        ? 'bg-green-100 text-green-800'
                                                                        : 'bg-orange-100 text-orange-800'
                                                                )}
                                                            >
                                                                {condition.is_met
                                                                    ? t(
                                                                          'section.condition.completed'
                                                                      )
                                                                    : t(
                                                                          'section.condition.pending'
                                                                      )}
                                                            </span>
                                                        </div>
                                                        <p
                                                            className={clsx(
                                                                'text-sm',
                                                                condition.is_met
                                                                    ? 'text-green-700'
                                                                    : 'text-orange-700'
                                                            )}
                                                        >
                                                            {getConditionHelp(
                                                                condition
                                                            )}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        )
                                    )}
                                </div>

                                {allConditionsMet && (
                                    <div className="mt-6 rounded-lg border border-green-200 bg-green-50 p-4">
                                        <div className="flex items-center space-x-2">
                                            <CheckCircle className="h-5 w-5 text-green-500" />
                                            <p className="font-medium text-green-800">
                                                {t(
                                                    'section.unlock.readyToUnlock'
                                                )}
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Actions */}
                        <div className="mt-8 border-t border-gray-200 pt-6">
                            <div className="flex flex-col gap-4 sm:flex-row">
                                <Link
                                    href={`/courses/${courseSlug}`}
                                    className={`inline-flex items-center justify-center rounded-md border px-4 py-2 font-medium transition-colors duration-200 ${colors.border} ${colors.buttonSecondary}`}
                                >
                                    {t('course.backToCourse')}
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LockedSectionPage;
